import { Link } from "react-router-dom";
import { AlertCircle, FileText, MoreHorizontal, Pencil } from "lucide-react";
import {
  Button,
  DataTable,
  DataTableEmpty,
  DataTableFooter,
  DataTableScroll,
  DataTableSkeleton,
  DataTableToolbar,
  DataTableToolbarSpacer,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  PageHeader,
  PageHeaderDescription,
  PageHeaderHeading,
  PageHeaderTitle,
  PageHeaderTop,
  StatusPill,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@eduviaos/ui-kit";
import type { FormDefinition } from "@eduviaos/shared/types";
import { useFormsList } from "@/hooks/useForms";
import { useCanEditForms } from "@/hooks/useFormsAuthoring";
import { TenantFormsPausedBanner } from "@/components/TenantFormsPausedBanner";
import { formEditorPath } from "@/pages/forms/formsRoutes";

/**
 * Phase F-finale-5 Slice 5a — IA tenant-self forms list.
 *
 * Lists every form the tenant owns (active versions) off `GET /forms`
 * and links each to the field editor (`/forms/{slug}/edit`). Honours
 * Iron Rule #16: sticky header in a scroll region, no zebra stripes,
 * `<StatusPill>` for status, action column last with a `…` dropdown,
 * skeleton (not spinner) loading, dedicated empty/error states.
 *
 * Reachable only when `useCanEditForms().canEditForms` (nav-gated), but
 * it defends the deep-link case too: when the SA has paused editing
 * (`pausedByTenant`) it shows the paused banner instead of edit affordances.
 */
const DATE_FMT = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "2-digit",
  year: "numeric",
});

function FormRow({ form, canEdit }: { form: FormDefinition; canEdit: boolean }) {
  const editTo = formEditorPath(form.slug);
  return (
    <TableRow>
      <TableCell className="font-medium">
        {canEdit ? (
          <Link to={editTo} className="hover:underline">
            {form.name}
          </Link>
        ) : (
          form.name
        )}
      </TableCell>
      <TableCell>
        <code className="bg-muted rounded px-1.5 py-0.5 text-xs">{form.purpose}</code>
      </TableCell>
      <TableCell className="text-right tabular-nums">v{form.version}</TableCell>
      <TableCell>
        <StatusPill status={form.requires_auth ? "inactive" : "active"}>
          {form.requires_auth ? "internal" : "public"}
        </StatusPill>
      </TableCell>
      <TableCell title={form.updated_at}>{DATE_FMT.format(new Date(form.updated_at))}</TableCell>
      <TableCell className="w-[1%] text-right">
        {canEdit ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label={`Actions for ${form.name}`}
                className="size-8"
              >
                <MoreHorizontal aria-hidden="true" className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem asChild>
                <Link to={editTo}>
                  <Pencil aria-hidden="true" className="size-3.5" />
                  Edit fields
                </Link>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
      </TableCell>
    </TableRow>
  );
}

export function FormsListPage() {
  const { canEditForms, pausedByTenant } = useCanEditForms();
  const formsQuery = useFormsList();

  const items = formsQuery.data?.items ?? [];
  const total = formsQuery.data?.total ?? 0;
  const isLoading = formsQuery.isLoading;
  const isError = formsQuery.isError;

  return (
    <div className="grid gap-1">
      <PageHeader>
        <PageHeaderTop>
          <PageHeaderHeading>
            <PageHeaderTitle>Forms</PageHeaderTitle>
            <PageHeaderDescription>
              Edit the fields on your institution&apos;s forms. Saving spawns a new active version;
              previous versions stay in history.
            </PageHeaderDescription>
          </PageHeaderHeading>
        </PageHeaderTop>
      </PageHeader>

      {pausedByTenant ? <TenantFormsPausedBanner className="mt-4" /> : null}

      {isError ? (
        <div
          role="alert"
          className="border-destructive/30 bg-destructive/5 text-destructive mt-4 flex items-start gap-2 rounded-md border px-3 py-2 text-xs"
        >
          <AlertCircle aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
          <span className="flex-1">Couldn&apos;t load your forms. Refresh to retry.</span>
        </div>
      ) : null}

      <DataTable className="mt-4">
        <DataTableToolbar>
          <DataTableToolbarSpacer />
          <span className="text-muted-foreground text-xs tabular-nums">
            {total.toLocaleString()} total
          </span>
        </DataTableToolbar>

        <DataTableScroll>
          <Table>
            <caption className="sr-only">Your institution&apos;s forms</caption>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[30%]">Name</TableHead>
                <TableHead className="w-[22%]">Purpose</TableHead>
                <TableHead className="w-[10%] text-right">Version</TableHead>
                <TableHead className="w-[14%]">Visibility</TableHead>
                <TableHead className="w-[16%]">Updated</TableHead>
                <TableHead className="w-[8%] text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            {isLoading ? (
              <DataTableSkeleton rows={5} columns={6} />
            ) : (
              <TableBody>
                {items.map((form) => (
                  <FormRow key={form.id} form={form} canEdit={canEditForms} />
                ))}
              </TableBody>
            )}
          </Table>
        </DataTableScroll>

        {!isLoading && items.length === 0 ? (
          <DataTableEmpty
            icon={<FileText aria-hidden="true" />}
            title={isError ? "Couldn't load forms" : "No forms yet"}
            description={
              isError
                ? "Refresh to retry — if it keeps failing, check the network."
                : "Your institution doesn't have any forms yet. Forms are seeded during onboarding; contact your account manager if you expected one here."
            }
          />
        ) : null}

        {!isLoading && total > 0 ? (
          <DataTableFooter>
            <span className="text-muted-foreground text-sm tabular-nums">
              Showing {items.length.toLocaleString()} of {total.toLocaleString()}
            </span>
          </DataTableFooter>
        ) : null}
      </DataTable>
    </div>
  );
}
